/**
 * US state codes — the single source of the values a venue address may carry
 * (BON-586).
 *
 * Two lists, because the set an operator picks from and the set the API accepts
 * are not the same. `US_STATES` is what the venue form offers. `US_STATE_CODES`
 * adds the territories and military post codes: we do not run workshops there,
 * but rejecting a legitimate USPS code at the route boundary would be a 400 no
 * operator could work around.
 *
 * The backend derives its zod `StateCode` from `US_STATE_CODES`
 * (`backend/src/lib/validation/schemas.ts`) and the venue form re-exports
 * `US_STATES` (`frontend/src/components/dashboards/venues/venueCreateFields.tsx`).
 * Kept zod-free so this package stays dependency-light and the enum is always
 * built by the consumer's own zod instance.
 */

/** The 50 states plus DC, ordered by the code the form actually renders —
 *  a name-ordered list reads as unsorted in a dropdown showing codes. */
export const US_STATES = [
  'AK',
  'AL',
  'AR',
  'AZ',
  'CA',
  'CO',
  'CT',
  'DC',
  'DE',
  'FL',
  'GA',
  'HI',
  'IA',
  'ID',
  'IL',
  'IN',
  'KS',
  'KY',
  'LA',
  'MA',
  'MD',
  'ME',
  'MI',
  'MN',
  'MO',
  'MS',
  'MT',
  'NC',
  'ND',
  'NE',
  'NH',
  'NJ',
  'NM',
  'NV',
  'NY',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VA',
  'VT',
  'WA',
  'WI',
  'WV',
  'WY',
] as const;

export type UsState = (typeof US_STATES)[number];

/** Inhabited territories. Accepted by the API, not offered in the form. */
export const US_TERRITORIES = ['PR', 'VI', 'GU', 'AS', 'MP'] as const;

/** Military post codes (Armed Forces Americas / Europe / Pacific). */
export const US_MILITARY_CODES = ['AA', 'AE', 'AP'] as const;

/** Every code the API accepts for an address. */
export const US_STATE_CODES = [...US_STATES, ...US_TERRITORIES, ...US_MILITARY_CODES] as const;

export type UsStateCode = (typeof US_STATE_CODES)[number];
